import { ApiError } from "../types"
import type { FormEvent } from "react"
import { errorMessage } from "../types"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { signIn } from "../api/auth"

export default function LoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [reactivationRequired, setReactivationRequired] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const user = await signIn(email, password, reactivationRequired)
      login(user)
      void navigate("/dashboard")
    } catch (err) {
      setError(errorMessage(err))
      setReactivationRequired(
        err instanceof ApiError && err.code === "account_inactive"
      )
    } finally {
      setLoading(false)
    }
  }

  function submitLabel() {
    if (loading) {
      return "Signing in..."
    }
    if (reactivationRequired) {
      return "Reactivate account"
    }
    return "Sign in"
  }

  return (
    <div className="min-h-screen flex items-start md:items-center justify-center px-4 py-8 bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-1">
          Gifting Made Easy
        </h1>
        <p className="text-gray-500 text-sm mb-8">Sign in to your account</p>

        {error && (
          <div
            role="alert"
            className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm"
          >
            {error}
          </div>
        )}

        <form
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
          className="space-y-4"
        >
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setReactivationRequired(false)
                setError(null)
              }}
              disabled={loading}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setReactivationRequired(false)
                setError(null)
              }}
              disabled={loading}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white min-h-11 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {submitLabel()}
          </button>
          {reactivationRequired && (
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                setReactivationRequired(false)
                setError(null)
                setPassword("")
              }}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Keep account deactivated
            </button>
          )}
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="text-blue-600 hover:underline font-medium"
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  )
}
